import { baseUrl } from '../../env'
import { DEFAULT_SPACE_NAME } from '../../utils/spaces'
import { SanitySmProduct } from '../sanity/types'
import { GetCountSanityProductParams, GetSanityProductParams, PlpSortStrategy } from './types'

export const getSanityProducts = async ({
  capacity,
  type,
  buildings,
  sortingParams,
  lastField,
  lastId,
  lastInquiryOnly,
  lastPlpRank,
  instantBook,
  sortStrategy = PlpSortStrategy.Standard,
}: GetSanityProductParams): Promise<SanitySmProduct[]> => {
  const url = new URL('api/products', baseUrl)
  const { field, order } = sortingParams

  if (!field || !order) return []

  if (buildings) url.searchParams.set('buildings', buildings?.length ? buildings.join(',') : DEFAULT_SPACE_NAME)
  if (lastField != null) url.searchParams.set('lastField', lastField.toString())
  if (lastId != null) url.searchParams.set('lastId', lastId)
  if (lastInquiryOnly !== null) url.searchParams.set('lastInquiryOnly', lastInquiryOnly.toString())
  if (lastPlpRank != null) url.searchParams.set('lastPlpRank', lastPlpRank.toString())
  if (capacity) url.searchParams.set('minimumCapacity', capacity)
  url.searchParams.set('paginate', 'true')
  url.searchParams.set('sortStrategy', sortStrategy)
  // Support both single type and array of types
  const typesArray = Array.isArray(type) ? type : [type]
  url.searchParams.set('spaceType', typesArray.join(','))
  url.searchParams.set('field', field)
  url.searchParams.set('order', order)
  url.searchParams.set('instantBook', instantBook.toString())

  const response = await fetch(url)
  const data = await response.json()

  if (response.status >= 500) {
    return []
  }

  if (!response.ok) {
    throw data
  }

  return data
}

export const getAllSanityProducts = async ({
  capacity,
  type,
  buildings,
  sortingParams,
  instantBook,
  sortStrategy = PlpSortStrategy.Standard,
}: Omit<GetSanityProductParams, 'lastId' | 'lastField' | 'lastInquiryOnly' | 'lastPlpRank'>): Promise<
  SanitySmProduct[]
> => {
  const url = new URL('api/products', baseUrl)
  const { field, order } = sortingParams

  if (!field || !order) return []

  if (buildings) url.searchParams.set('buildings', buildings?.length ? buildings.join(',') : DEFAULT_SPACE_NAME)
  if (capacity) url.searchParams.set('minimumCapacity', capacity)
  url.searchParams.set('paginate', 'false')
  url.searchParams.set('sortStrategy', sortStrategy)
  // Support both single type and array of types
  const typesArray = Array.isArray(type) ? type : [type]
  url.searchParams.set('spaceType', typesArray.join(','))
  url.searchParams.set('field', field)
  url.searchParams.set('order', order)
  url.searchParams.set('instantBook', instantBook.toString())

  const response = await fetch(url)
  const data = await response.json()

  if (response.status >= 500) {
    return []
  }

  if (!response.ok) {
    throw data
  }

  return data
}

export const getCountSanityProducts = async ({
  capacity,
  type,
  buildings,
  instantBook,
}: GetCountSanityProductParams): Promise<number> => {
  const url = new URL('api/products/count', baseUrl)

  if (buildings) url.searchParams.set('buildings', buildings?.length ? buildings.join(',') : DEFAULT_SPACE_NAME)
  if (capacity) url.searchParams.set('minimumCapacity', capacity)
  // Support both single type and array of types
  const typesArray = Array.isArray(type) ? type : [type]
  url.searchParams.set('spaceType', typesArray.join(','))
  url.searchParams.set('instantBook', instantBook.toString())

  const response = await fetch(url)
  const data = await response.json()

  if (response.status >= 500 || !response.ok) {
    return 0
  }

  return data.count
}
